/**
 * @fileoverview Session Templates React Query Hooks
 * @author Hospital Room Scheduler Team
 * @created 10-10-2025 19:13:45
 * 
 * React Query hooks for session template operations (global templates).
 * Provides data fetching, caching, and mutation functionality for session templates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sessionTemplatesApi from '../../api/endpoints/sessionTemplates';
import type {
  SessionTemplate,
  CreateSessionTemplateData,
  UpdateSessionTemplateData,
  GetSessionTemplatesParams,
  AssignSessionTemplatesToRoomData
} from '../../api/endpoints/sessionTemplates';

// =============================================================================
// QUERY KEYS
// =============================================================================

const sessionTemplatesKeys = {
  all: ['session-templates'] as const,
  lists: () => [...sessionTemplatesKeys.all, 'list'] as const,
  list: (params: GetSessionTemplatesParams) => [...sessionTemplatesKeys.lists(), params] as const,
  details: () => [...sessionTemplatesKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionTemplatesKeys.details(), id] as const,
  stats: () => [...sessionTemplatesKeys.all, 'stats'] as const,
  roomTemplates: (roomId: string) => ['rooms', roomId, 'session-templates'] as const
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all session templates (global, no room filter)
 */
export function useSessionTemplates(params?: GetSessionTemplatesParams) {
  return useQuery({
    queryKey: sessionTemplatesKeys.list(params || {}),
    queryFn: () => sessionTemplatesApi.getAllSessionTemplates(params)
  });
}

/**
 * Fetch a single session template by ID
 */
export function useSessionTemplate(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: sessionTemplatesKeys.detail(id),
    queryFn: () => sessionTemplatesApi.getSessionTemplateById(id),
    enabled: enabled && !!id
  });
}

/**
 * Fetch session template statistics
 */
export function useSessionTemplateStats() {
  return useQuery({
    queryKey: sessionTemplatesKeys.stats(),
    queryFn: () => sessionTemplatesApi.getSessionTemplateStats()
  });
}

/**
 * Fetch session templates assigned to a specific room
 */
export function useRoomSessionTemplates(roomId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: sessionTemplatesKeys.roomTemplates(roomId),
    queryFn: () => sessionTemplatesApi.getRoomSessionTemplates(roomId),
    enabled: enabled && !!roomId
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new session template
 */
export function useCreateSessionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSessionTemplateData) => 
      sessionTemplatesApi.createSessionTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.stats() });
    }
  });
}

/**
 * Update an existing session template
 */
export function useUpdateSessionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSessionTemplateData }) =>
      sessionTemplatesApi.updateSessionTemplate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.stats() });
    }
  });
}

/**
 * Delete a session template
 */
export function useDeleteSessionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sessionTemplatesApi.deleteSessionTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.stats() });
    }
  });
}

/**
 * Assign session templates to a room
 */
export function useAssignSessionTemplatesToRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roomId, data }: { roomId: string; data: AssignSessionTemplatesToRoomData }) =>
      sessionTemplatesApi.assignSessionTemplatesToRoom(roomId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.roomTemplates(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: ['rooms', variables.roomId] });
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.stats() });
    }
  });
}

/**
 * Remove a session template from a room
 */
export function useRemoveSessionTemplateFromRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roomId, templateId }: { roomId: string; templateId: string }) =>
      sessionTemplatesApi.removeSessionTemplateFromRoom(roomId, templateId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sessionTemplatesKeys.roomTemplates(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: ['rooms', variables.roomId] });
    }
  });
}

